import React, { useState } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Phone, Mail, MapPin, Send, CheckCircle, AlertCircle } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const ContactPage = () => {
  const { t } = useLanguage();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // 'success' | 'error' | null

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      await axios.post(`${API_URL}/api/contact`, formData);
      setSubmitStatus('success');
      setFormData({ name: '', email: '', phone: '', message: '' });
    } catch (error) {
      console.error('Error submitting form:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactInfo = [
    {
      icon: Phone,
      label: t('contact.info.phone'),
      value: '+381 63 162 7662',
      href: 'tel:+381631627662'
    },
    {
      icon: Mail,
      label: t('contact.info.email'),
      value: 'radesandraerakovic@gmail.com',
      href: 'mailto:radesandraerakovic@gmail.com'
    },
    {
      icon: MapPin,
      label: t('contact.info.address'),
      value: 'Gornji Milanovac - Brusnica bb',
      href: null
    }
  ];

  return (
    <main className="pt-24" data-testid="contact-page">
      {/* Hero Section */}
      <section className="bg-[#0B1E3B] py-20" data-testid="contact-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="text-[#D4AF37] text-sm font-semibold tracking-widest uppercase mb-4 block">
            {t('contact.tagline')}
          </span>
          <h1 className="text-5xl md:text-6xl font-bold text-white font-['Barlow_Condensed'] mb-4">
            {t('contact.title')}
          </h1>
          <p className="text-gray-300 text-lg max-w-2xl mx-auto">
            {t('contact.subtitle')}
          </p>
        </div>
      </section>

      {/* Contact Section */}
      <section className="bg-white py-16" data-testid="contact-content">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Contact Form */}
            <div className="bg-[#F8F9FA] rounded-2xl p-8" data-testid="contact-form-container">
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <Label htmlFor="name" className="text-[#0B1E3B] font-medium">
                    {t('contact.form.name')} *
                  </Label>
                  <Input
                    id="name"
                    name="name"
                    type="text"
                    required
                    value={formData.name}
                    onChange={handleChange}
                    className="mt-2 bg-white border-gray-200 focus:border-[#D4AF37] focus:ring-[#D4AF37]"
                    data-testid="contact-form-name"
                  />
                </div>

                <div>
                  <Label htmlFor="email" className="text-[#0B1E3B] font-medium">
                    {t('contact.form.email')} *
                  </Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    className="mt-2 bg-white border-gray-200 focus:border-[#D4AF37] focus:ring-[#D4AF37]"
                    data-testid="contact-form-email"
                  />
                </div>

                <div>
                  <Label htmlFor="phone" className="text-[#0B1E3B] font-medium">
                    {t('contact.form.phone')}
                  </Label>
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleChange}
                    className="mt-2 bg-white border-gray-200 focus:border-[#D4AF37] focus:ring-[#D4AF37]"
                    data-testid="contact-form-phone"
                  />
                </div>

                <div>
                  <Label htmlFor="message" className="text-[#0B1E3B] font-medium">
                    {t('contact.form.message')} *
                  </Label>
                  <Textarea
                    id="message"
                    name="message"
                    required
                    rows={5}
                    value={formData.message}
                    onChange={handleChange}
                    className="mt-2 bg-white border-gray-200 focus:border-[#D4AF37] focus:ring-[#D4AF37] resize-none"
                    data-testid="contact-form-message"
                  />
                </div>

                {/* Status Messages */}
                {submitStatus === 'success' && (
                  <div className="flex items-center gap-2 text-green-600 bg-green-50 p-4 rounded-lg" data-testid="form-success">
                    <CheckCircle className="h-5 w-5" />
                    <span>{t('contact.form.success')}</span>
                  </div>
                )}
                {submitStatus === 'error' && (
                  <div className="flex items-center gap-2 text-red-600 bg-red-50 p-4 rounded-lg" data-testid="form-error">
                    <AlertCircle className="h-5 w-5" />
                    <span>{t('contact.form.error')}</span>
                  </div>
                )}

                <Button
                  type="submit"
                  size="lg"
                  disabled={isSubmitting}
                  className="w-full bg-[#0B1E3B] text-white hover:bg-[#D4AF37] hover:text-[#0B1E3B] font-semibold"
                  data-testid="contact-form-submit"
                >
                  {isSubmitting ? (
                    <>
                      <span className="animate-spin mr-2">⏳</span>
                      {t('contact.form.sending')}
                    </>
                  ) : (
                    <>
                      <Send className="mr-2 h-5 w-5" />
                      {t('contact.form.send')}
                    </>
                  )}
                </Button>
              </form>
            </div>

            {/* Contact Info & Map */}
            <div className="space-y-8">
              {/* Contact Cards */}
              <div className="space-y-4">
                {contactInfo.map((info, index) => (
                  <div 
                    key={index}
                    className="flex items-start gap-4 p-4 bg-[#F8F9FA] rounded-xl"
                    data-testid={`contact-info-${index}`}
                  >
                    <div className="w-12 h-12 bg-[#0B1E3B] rounded-lg flex items-center justify-center flex-shrink-0">
                      <info.icon className="h-5 w-5 text-[#D4AF37]" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-500 uppercase tracking-wider">{info.label}</p>
                      {info.href ? (
                        <a 
                          href={info.href}
                          className="text-[#0B1E3B] font-semibold hover:text-[#D4AF37] transition-colors"
                        >
                          {info.value}
                        </a>
                      ) : (
                        <p className="text-[#0B1E3B] font-semibold">{info.value}</p>
                      )}
                    </div>
                  </div>
                ))}

                {/* Viber Button */}
                <a 
                  href="viber://chat?number=+381631627662"
                  className="flex items-center gap-4 p-4 bg-[#F8F9FA] rounded-xl hover:bg-purple-50 transition-colors group"
                  data-testid="contact-viber"
                >
                  <div className="w-12 h-12 viber-btn rounded-lg flex items-center justify-center flex-shrink-0">
                    <svg className="h-5 w-5 text-white" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12.031 1.003c-4.496-.062-8.795 1.727-10.615 5.83C.1 9.67-.246 12.623.218 15.86c.328 2.294 1.027 4.24 2.378 5.93.636.793 1.26 1.59 1.866 2.405.32.431.625.87.94 1.305.168.233.343.463.503.703.108.163.202.336.309.5.045.068.196.289.196.289l-.008.008c-.233.31-1.106 1.28-1.106 1.28l.012-.004s.367.36.875.36c.196 0 .423-.055.65-.193.544-.332 2.413-1.615 3.246-2.235.15-.111.333-.172.521-.172h.2c1.623.102 3.251.097 4.87-.075 2.06-.219 3.984-.79 5.664-1.99 2.527-1.805 3.886-4.324 4.267-7.365.278-2.224.023-4.363-.836-6.397C22.5 5.582 19.36 2.608 15.022 1.392c-.99-.277-1.993-.39-2.991-.389zm-.044 1.643c.876.002 1.77.1 2.663.34 3.633.975 6.305 3.514 7.46 7.132.692 2.173.681 4.369.004 6.545-.74 2.38-2.24 4.152-4.474 5.228-1.663.801-3.449 1.072-5.28 1.072-.7 0-1.409-.033-2.116-.092l-.204-.017a1.97 1.97 0 0 0-1.256.407c-.723.539-2.323 1.638-2.836 1.953.19-.253.85-1.115.991-1.295l.004-.006c.29-.386.245-.921-.11-1.252a79.527 79.527 0 0 1-1.104-1.096l-.13-.133c-.95-1.043-1.68-2.2-2.075-3.567-.565-1.953-.508-3.91.146-5.822.855-2.492 2.543-4.256 4.902-5.362 1.388-.65 2.86-.993 4.402-1.031.346-.01.694-.014 1.04-.013z"/>
                    </svg>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500 uppercase tracking-wider">{t('contact.info.viber')}</p>
                    <p className="text-[#0B1E3B] font-semibold group-hover:text-purple-600 transition-colors">+381 63 162 7662</p>
                  </div>
                </a>
              </div>

              {/* Map */}
              <div className="rounded-xl overflow-hidden shadow-md h-72" data-testid="contact-map">
                <iframe 
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d46242.80432714848!2d20.428824!3d43.9832!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x4756094d10c19e93%3A0x87ca1cc8cf02e46a!2sGornji%20Milanovac!5e0!3m2!1sen!2srs!4v1699999999999!5m2!1sen!2srs"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Lokacija - Gornji Milanovac"
                ></iframe>
              </div>

              {/* Work Area */}
              <div className="bg-[#0B1E3B] rounded-xl p-6 text-white" data-testid="contact-work-area">
                <h3 className="text-xl font-bold font-['Barlow_Condensed'] text-[#D4AF37] mb-2">
                  {t('contact.area.title')}
                </h3>
                <p className="text-gray-300 text-sm leading-relaxed">
                  {t('contact.area.description')}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};
